/**
 * EditClassificationDialog - Dialog para editar classificação de template existente
 * 
 * Permite ao usuário modificar nome, cor e descrição de uma classificação.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { showToast } from "@/lib/toast";
import { logger } from "@/lib/logger";

// Cores pré-definidas para classificações
const CLASSIFICATION_COLORS = [
  { name: "Verde", hex: "#10B981" },
  { name: "Azul", hex: "#3B82F6" },
  { name: "Amarelo", hex: "#F59E0B" },
  { name: "Roxo", hex: "#8B5CF6" },
  { name: "Rosa", hex: "#EC4899" },
  { name: "Vermelho", hex: "#EF4444" },
  { name: "Ciano", hex: "#06B6D4" },
  { name: "Cinza", hex: "#6B7280" },
];

// Interface para classificação
interface TemplateClassification {
  id: string;
  name: string;
  color: string;
  description: string | null;
}

// Props do componente
interface EditClassificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classification: TemplateClassification | null;
  onSuccess: () => void;
}

export function EditClassificationDialog({
  open,
  onOpenChange,
  classification,
  onSuccess,
}: EditClassificationDialogProps) {
  // Estados do formulário
  const [name, setName] = useState("");
  const [color, setColor] = useState(CLASSIFICATION_COLORS[0].hex);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Preenche o formulário quando a classificação é carregada
  useEffect(() => {
    if (classification) {
      setName(classification.name);
      setColor(classification.color);
      setDescription(classification.description || "");
    }
  }, [classification]);

  /**
   * Reseta o formulário para os valores iniciais
   */
  const resetForm = () => {
    setName("");
    setColor(CLASSIFICATION_COLORS[0].hex);
    setDescription("");
  };

  /**
   * Valida os campos do formulário
   */
  const validateForm = (): boolean => {
    if (!name.trim()) {
      showToast.error("Nome é obrigatório");
      return false;
    }

    if (name.trim().length < 3) {
      showToast.error("Nome deve ter pelo menos 3 caracteres");
      return false;
    }

    if (name.trim().length > 50) {
      showToast.error("Nome deve ter no máximo 50 caracteres");
      return false;
    }

    if (description.length > 200) {
      showToast.error("Descrição deve ter no máximo 200 caracteres");
      return false;
    }

    return true;
  };

  /**
   * Submete o formulário e atualiza a classificação
   */
  const handleSubmit = async () => {
    if (!classification) return;

    if (!validateForm()) return;

    setSaving(true);
    try {
      // Verifica duplicidade de nome (excluindo a própria classificação)
      const { data: existing, error: checkError } = await supabase
        .from("template_classifications")
        .select("id")
        .neq("id", classification.id)
        .ilike("name", name.trim())
        .maybeSingle();

      if (checkError) {
        logger.error("[EditClassification] Erro ao verificar duplicidade:", checkError.message);
        showToast.error("Erro ao verificar nome");
        return;
      }

      if (existing) {
        showToast.error("Já existe uma classificação com este nome");
        return;
      }

      // Atualiza a classificação
      const { error: updateError } = await supabase
        .from("template_classifications")
        .update({
          name: name.trim(),
          color: color,
          description: description.trim() || null,
        })
        .eq("id", classification.id);

      if (updateError) {
        logger.error("[EditClassification] Erro ao atualizar:", updateError.message);
        showToast.error("Erro ao atualizar classificação");
        return;
      }

      showToast.success("Classificação atualizada com sucesso");
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      logger.error("[EditClassification] Exceção:", err);
      showToast.error("Erro ao atualizar classificação");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Fecha o dialog e reseta o formulário
   */
  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Classificação</DialogTitle>
          <DialogDescription>
            Modifique os dados da classificação.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Campo Nome */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">Nome *</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Comercial, Suporte, Cobrança..."
              maxLength={50}
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              {name.length}/50 caracteres
            </p>
          </div>

          {/* Seletor de Cor */}
          <div className="space-y-2">
            <Label>Cor *</Label>
            <div className="flex flex-wrap gap-2">
              {CLASSIFICATION_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColor(c.hex)}
                  disabled={saving}
                  className={`w-8 h-8 rounded-full transition-all ${
                    color === c.hex
                      ? "ring-2 ring-offset-2 ring-primary"
                      : "hover:scale-110"
                  }`}
                  style={{ backgroundColor: c.hex }}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          {/* Campo Descrição */}
          <div className="space-y-2">
            <Label htmlFor="edit-description">Descrição (opcional)</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrição sobre o uso desta classificação..."
              maxLength={200}
              rows={3}
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/200 caracteres
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
